"use client";
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import React, { useCallback, useMemo, useRef, useState } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "../../icons/index.js";
import { useCx } from "../../theming/make-styles.js";
import DropdownBase from "../dropdown-base/dropdown-base.js";
import List, { ListItem } from "../list/index.js";
import useCombinedRefs from "../utils/use-combined-refs.js";
import useKeyboardNavigation from "../utils/use-keyboard-navigation.js";
export const Dropdown = React.forwardRef(function Dropdown(_a, ref) {
    var { children, color = "primary", preventSpaces = true, strategy = "align-left", value, onClose, onSelect } = _a, props = __rest(_a, ["children", "color", "preventSpaces", "strategy", "value", "onClose", "onSelect"]);
    const [submenus, setSubmenus] = useState([]);
    const innerRef = useRef(null);
    const openRef = useRef(null);
    const combinedRef = useCombinedRefs(ref, innerRef);
    const cx = useCx();
    const currentChildren = useMemo(function () {
        const result = React.Children.map((submenus.length === 0 ? children : submenus[0].props.children), x => x);
        return submenus.length === 0 ? result : [submenus[0], ...result];
    }, [children, submenus]);
    const items = useMemo(() => Object.fromEntries(React.Children.map(currentChildren, child => child)
        .filter(c => !!c)
        .map(c => [c.props.value, c])), [currentChildren]);
    const labels = useMemo(() => Object.keys(items).filter(el => !items[el].props.disabled), [items]);
    const { current: preselection, handleKeyDown, setPreselection, } = useKeyboardNavigation(labels, (action, current, event) => {
        var _a, _b, _c;
        if (action === "close") {
            (_a = openRef.current) === null || _a === void 0 ? void 0 : _a.setOpen(false);
        }
        else if (action === "toggle") {
            if (!((_b = openRef.current) === null || _b === void 0 ? void 0 : _b.open)) {
                (_c = openRef.current) === null || _c === void 0 ? void 0 : _c.setOpen(true);
            }
            else if (current) {
                handleAct(current, event);
            }
        }
    }, preventSpaces);
    const handleAct = useCallback((value, event) => {
        var _a, _b, _c, _d, _e;
        if (!event.bubbles && event.isPropagationStopped()) {
            return;
        }
        const current = items[value];
        if ("button" in event) {
            (_b = current === null || current === void 0 ? void 0 : (_a = current.props).onClick) === null || _b === void 0 ? void 0 : _b.call(_a, event);
        }
        if (current.type === DropdownItem) {
            if (value)
                onSelect === null || onSelect === void 0 ? void 0 : onSelect(value);
            (_c = innerRef.current) === null || _c === void 0 ? void 0 : _c.focus();
            (_d = openRef.current) === null || _d === void 0 ? void 0 : _d.setOpen(false);
            setSubmenus([]);
        }
        else if (current && current.props.value === ((_e = submenus[0]) === null || _e === void 0 ? void 0 : _e.props.value)) {
            setSubmenus(submenus.slice(1));
        }
        else if (current) {
            setSubmenus([current, ...submenus]);
        }
    }, [items, submenus, onSelect]);
    return (_jsx(DropdownBase, Object.assign({ ref: combinedRef, "data-component": "dropdown", openRef: openRef, strategy: strategy, triggerStrategy: "toggle" }, props, { onClose: () => {
            setSubmenus([]);
            onClose === null || onClose === void 0 ? void 0 : onClose();
        }, onKeyDown: handleKeyDown, children: _jsx(List, { color: color, hoverable: true, role: "menu", children: React.Children.map(currentChildren, (child, index) => {
                var _a, _b;
                if (!child)
                    return null;
                const { type, props, key } = child;
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
                const { children, className, disabled, value: itemValue, onClick } = props, rest = __rest(props, ["children", "className", "disabled", "value", "onClick"]);
                let label = null;
                if ("label" in rest) {
                    // eslint-disable-next-line prefer-destructuring
                    label = rest.label;
                    delete rest.label;
                }
                return (_jsx(ListItem, Object.assign({ active: type === DropdownItem && !!value && itemValue === value, className: cx(className), "data-item-id": key || index, disabled: disabled, hoverable: itemValue === preselection, leftIcon: (label || typeof label === "number") && itemValue === ((_a = submenus[0]) === null || _a === void 0 ? void 0 : _a.props.value) ? ChevronLeftIcon : undefined, rightIcon: (label || typeof label === "number") && itemValue !== ((_b = submenus[0]) === null || _b === void 0 ? void 0 : _b.props.value) ? ChevronRightIcon : undefined, role: "menuitem", onClick: e => {
                        if (disabled || !itemValue)
                            return;
                        handleAct(itemValue, e);
                    }, onMouseEnter: () => setPreselection(itemValue), onMouseLeave: () => setPreselection() }, rest, { children: label || typeof label === "number" ? label : undefined || children }), key || index));
            }) }) })));
});
export const DropdownItem = (_props) => null;
export const DropdownSubmenu = (_props) => null;
export default Dropdown;
//# sourceMappingURL=dropdown.js.map